// Shared Environment config slice for decomposed panels
// (docs/editor-consolidation/panel-content-spec.md §3.2).
//
// Environment is the one page in the decomposition that never used
// useModeDocument: it persists into project.config.environment (deriving
// project.config.theme alongside it) via its own hand-rolled debounced
// saver. Splitting it into sibling panels needs the same shape as §3.1's
// store - one shared load-once value, one shared debounced save,
// reference-counted across every mounted panel - just keyed by projectId
// alone rather than (projectId, ModeDocKind), since it round-trips through
// saveProjectConfig, not the docs service.
import { useCallback, useEffect, useReducer, useRef } from 'react';

import { toast } from '../../design/toast';
import type { StudioProjectConfig } from '../../../engine';
import { useEditorHost } from '../EditorHost';
import { saveProjectConfig } from '../../persistence/projectRepository';
import { createDebouncedSaver, type DebouncedSaver } from '../debouncedSave';
import { themeFromEnvironment } from '../editor-modes3';

const SAVE_DEBOUNCE_MS = 1200;

export interface EnvironmentValues {
  preset: string;
  time: number;
  fog: number;
  sun: number;
  bloom: number;
  vignette: number;
}

function valuesFrom(stored: StudioProjectConfig['environment']): EnvironmentValues {
  return {
    preset: stored?.preset ?? 'Golden hour',
    time: stored?.time ?? 17,
    fog: stored?.fog ?? 28,
    sun: stored?.sun ?? 70,
    bloom: stored?.bloom ?? 0,
    vignette: stored?.vignette ?? 0,
  };
}

interface Entry {
  values: EnvironmentValues;
  refCount: number;
  saver: DebouncedSaver<StudioProjectConfig>;
  listeners: Set<() => void>;
}

const entries = new Map<string, Entry>();

function notify(entry: Entry): void {
  entry.listeners.forEach((l) => l());
}

function ensureEntry(projectId: string, initial: StudioProjectConfig['environment']): Entry {
  const existing = entries.get(projectId);
  if (existing) return existing;
  const entry: Entry = {
    values: valuesFrom(initial),
    refCount: 0,
    listeners: new Set(),
    saver: createDebouncedSaver<StudioProjectConfig>((config, { keepalive }) => {
      void saveProjectConfig(projectId, config, { keepalive }).catch((err: unknown) => {
        toast('Environment settings failed to save: ' + (err instanceof Error ? err.message : 'unknown error'), 'error');
      });
    }, SAVE_DEBOUNCE_MS),
  };
  entries.set(projectId, entry);
  return entry;
}

function acquire(projectId: string, initial: StudioProjectConfig['environment']): Entry {
  const entry = ensureEntry(projectId, initial);
  entry.refCount += 1;
  return entry;
}

function release(projectId: string): void {
  const entry = entries.get(projectId);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount <= 0) {
    entry.saver.flush();
    entries.delete(projectId);
  }
}

export interface EnvironmentSliceState {
  values: EnvironmentValues;
  /** Merges `patch` into the shared values in one update (so a preset click
   * setting preset+time+sun+fog together produces one re-render and one
   * debounce reschedule, not four) and schedules the derived config save. */
  update: (patch: Partial<EnvironmentValues>) => void;
  flush: () => void;
}

/**
 * Shared Environment settings, split from EnvironmentRoute's own local
 * useState bag. Every consumer sees the same values; any consumer's update
 * is visible to every sibling panel immediately.
 */
export function useSharedEnvironmentSlice(): EnvironmentSliceState {
  const { projectId, project } = useEditorHost();
  const initialRef = useRef(project.config.environment);
  const [, forceRender] = useReducer((c: number) => c + 1, 0);

  useEffect(() => {
    const entry = acquire(projectId, initialRef.current);
    entry.listeners.add(forceRender);
    forceRender();
    return () => {
      entry.listeners.delete(forceRender);
      release(projectId);
    };
  }, [projectId]);

  const update = useCallback(
    (patch: Partial<EnvironmentValues>) => {
      const entry = entries.get(projectId);
      if (!entry) return;
      entry.values = { ...entry.values, ...patch };
      notify(entry);
      const { preset, time, fog, sun, bloom, vignette } = entry.values;
      const theme = themeFromEnvironment(preset, time, fog, project.config.theme, { sun, bloom, vignette });
      const nextConfig: StudioProjectConfig = {
        ...project.config,
        environment: entry.values,
        theme,
      };
      entry.saver.schedule(nextConfig);
    },
    [projectId, project],
  );

  const flush = useCallback(() => {
    entries.get(projectId)?.saver.flush();
  }, [projectId]);

  const entry = entries.get(projectId);
  return { values: entry?.values ?? valuesFrom(initialRef.current), update, flush };
}
